"use client";

import { FileSearch, X } from "lucide-react";
import { useFeatureFlagEnabled, usePostHog } from "posthog-js/react";
import { useEffect, useRef, useState, useTransition } from "react";
import { Button } from "@/components/ui/button";
import { startToolCheckout } from "@/lib/checkout-api";
import { isApplyMomentClaimed } from "@/lib/experiments/job-intent-signup";
import { useHydrated } from "@/lib/hydration/use-hydrated";
import type { Locale } from "@/lib/i18n/config";
import { useStickyFooterClaimed } from "@/lib/monetization/sticky-footer-claim";
import { APPLY_CLICKED_EVENT, clearPostApplyPending } from "./post-apply-offer";

/**
 * The paid offer at the apply moment (Experiment A, revenue-rescue 2026-09-04).
 *
 * Why here and not on /pricing: in August 2026, 14,451 human visitors produced
 * 56 unique pricing-page views (0.39%) and zero external payments, while ~168
 * people/month reached the outbound-apply click and were asked for nothing at
 * all. The apply click is the highest-intent moment the product has, and it was
 * the only one with no offer on it. The same month's `apply-paywall-relaxed`
 * read-out showed re-tightening the apply wall recovers approximately nothing
 * (61 vs 59 unique pricing viewers), so moving the offer is the lever, not
 * restoring the gate.
 *
 * It sells `cv_review`, an EXISTING one-time SKU with a working rail end to end
 * (checkout -> Mayar -> webhook -> `runCvReviewGeneration` -> emailed report).
 * Nothing new is charged for; this is a placement, which is exactly the scope
 * of the monetization-freeze carve-out logged 2026-09-04. It is one-time, never
 * recurring.
 *
 * WHY THIS IS A FIXED OVERLAY AND NOT AN IN-CONTENT CARD (2026-09-06).
 * It shipped in-content, mounted next to `AfterApplyInline` near the end of the
 * job page. Measured on live prod, twice: the card landed at 4,346px and 4,212px
 * from the top of a 5,398px / 5,264px document — 80-81% down the page, **6.1-6.3
 * viewport heights below the fold** — while the Apply CTA the user had just
 * clicked sat at 232px. An external apply opens in a new tab, so the visitor
 * returns to a page still scrolled at the top, six screens above an offer they
 * never scroll to. Six `apply_offer_shown` events fired in a day with zero
 * clicks and zero dismissals, which read like indifference and was really just
 * an off-screen render.
 *
 * That also made `apply_offer_shown` a lie: it fires on RENDER, and an
 * in-content card renders wherever the DOM puts it, seen or not. Fixed
 * positioning makes the impression honest by construction — if it rendered, it
 * was on screen — without needing an IntersectionObserver to police it.
 *
 * The pattern is borrowed from `PostApplyOffer`, the one surface with a real
 * click record at this exact moment: 94 impressions, 4 clicks, 4.3% CTR. The
 * in-content slot next door has 2 impressions in its entire life.
 *
 * Non-negotiables baked in here:
 *  - It NEVER blocks or delays the apply. The apply opens in its own tab on
 *    click; this reveals in the tab the user left behind.
 *  - It is dismissible, and the dismissal is remembered for the session.
 *  - `apply-moment-cv-offer` is the SOLE switch (default OFF => inert). No
 *    hardcoded constant shadows it.
 *
 * No price is printed in this copy on purpose. The IDR and USD rails resolve
 * different amounts for the same SKU (`cv_review` is Rp 12.000 on the Mayar
 * rail and $7 on the global rail), and this surface has no reliable market at
 * render time — the job page is deliberately session- and header-free so it
 * stays cacheable. Printing one number here would break display==charge parity
 * for the other rail (the defect class PR #2968 exists to prevent). The exact,
 * market-correct amount is shown on the checkout page before any money moves.
 * Show a price here only once this surface can resolve a market.
 */

const DISMISS_KEY = "ld_apply_offer_dismissed";

const COPY = {
	id: {
		title: "Sambil menunggu balasan",
		body: "Kami cek CV kamu terhadap lowongan ini: skor ATS, kata kunci yang hilang, dan apa yang perlu diperbaiki sebelum lamaran berikutnya.",
		cta: "Cek CV saya (berbayar, sekali bayar)",
		dismiss: "Tutup",
		error: "Checkout gagal dibuka. Coba lagi.",
		unavailable: "Pembayaran belum tersedia di wilayah kamu.",
	},
	en: {
		title: "While you wait to hear back",
		body: "We'll score your CV against this job: ATS score, the keywords you're missing, and what to fix before the next application.",
		cta: "Score my CV (paid, one-time)",
		dismiss: "Dismiss",
		error: "Could not open checkout. Try again.",
		unavailable: "Payment isn't available in your region yet.",
	},
} as const;

function wasDismissed(): boolean {
	try {
		return sessionStorage.getItem(DISMISS_KEY) === "1";
	} catch {
		return false;
	}
}

export function ApplyMomentCvOffer({
	jobId,
	locale,
}: {
	jobId: string;
	locale: Locale;
}) {
	const hydrated = useHydrated();
	const enabled = useFeatureFlagEnabled("apply-moment-cv-offer");
	// Never paint over the mobile apply footer (`job-apply-cta.tsx`, z-40, same
	// bottom-0 slot). Same claim protocol `PostApplyOffer` uses — see
	// sticky-footer-claim.tsx for the "ambient nag covers the primary CTA" class
	// it closes. A missed sale is recoverable; a covered Apply button is not.
	const stickyFooterClaimed = useStickyFooterClaimed();
	const posthog = usePostHog();
	const [applied, setApplied] = useState(false);
	const [dismissed, setDismissed] = useState(false);
	const [error, setError] = useState("");
	const [pending, startCheckout] = useTransition();
	const shownRef = useRef(false);

	const t = COPY[locale === "id" ? "id" : "en"];

	// Listen regardless of flag readiness so a click landing before PostHog
	// resolves the flag is not missed — the reveal itself is gated below.
	useEffect(() => {
		const onApply = (e: Event) => {
			const detail = (e as CustomEvent<{ jobId?: string }>).detail;
			if (detail?.jobId === jobId) setApplied(true);
		};
		window.addEventListener(APPLY_CLICKED_EVENT, onApply);
		return () => window.removeEventListener(APPLY_CLICKED_EVENT, onApply);
	}, [jobId]);

	const show =
		applied &&
		hydrated &&
		enabled === true &&
		!dismissed &&
		!stickyFooterClaimed;

	useEffect(() => {
		if (!show || shownRef.current) return;
		// `job-intent-signup` test arm took this apply moment: one ask per apply.
		if (wasDismissed() || isApplyMomentClaimed(jobId)) {
			setDismissed(true);
			return;
		}
		shownRef.current = true;
		// One offer per apply: take the slot so the tab-return affiliate popup
		// self-suppresses rather than stacking a second card on the same click.
		clearPostApplyPending();
		posthog?.capture("apply_offer_shown", {
			job_id: jobId,
			product_key: "cv_review",
			locale,
		});
	}, [show, jobId, locale, posthog]);

	if (!show) return null;

	const onDismiss = () => {
		setDismissed(true);
		try {
			sessionStorage.setItem(DISMISS_KEY, "1");
		} catch {
			/* sessionStorage unavailable — dismissal just isn't remembered */
		}
		posthog?.capture("apply_offer_dismissed", { job_id: jobId, locale });
	};

	const onBuy = () => {
		setError("");
		posthog?.capture("apply_offer_cta_clicked", {
			job_id: jobId,
			product_key: "cv_review",
			locale,
		});
		startCheckout(async () => {
			const result = await startToolCheckout("cv_review");
			if ("error" in result) {
				if (result.error === "not_authenticated") {
					// Anonymous visitors are ~39% of apply clicks. Send them to the
					// tool itself rather than a bare login wall: they get the free
					// score first and can buy from there, so the offer never
					// dead-ends on an auth bounce.
					window.location.href = `/${locale}/tools/cek-ats-cv`;
					return;
				}
				setError(
					result.error === "billing_unavailable" ? t.unavailable : t.error,
				);
				return;
			}
			window.location.href = result.url;
		});
	};

	return (
		<div className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-sm rounded-3xl border border-border bg-card p-5 shadow-lg sm:left-auto sm:right-4 sm:mx-0">
			<div className="flex items-start justify-between gap-3">
				<p className="flex items-center gap-2 text-sm font-semibold text-foreground">
					<FileSearch aria-hidden="true" className="size-4 shrink-0" />
					{t.title}
				</p>
				<button
					type="button"
					onClick={onDismiss}
					aria-label={t.dismiss}
					className="-m-1 rounded-full p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
				>
					<X aria-hidden="true" className="size-4" />
				</button>
			</div>
			<p className="mt-2 text-sm text-muted-foreground">{t.body}</p>
			<Button
				onClick={onBuy}
				disabled={pending}
				size="md"
				shape="pill"
				className="mt-4 min-h-11"
			>
				{t.cta}
			</Button>
			{error ? (
				<p role="alert" className="mt-2 text-sm text-destructive">
					{error}
				</p>
			) : null}
		</div>
	);
}
